import React, { useContext, useEffect, useRef, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CarrierDetailGroupsOutDTO,
  CatalogCarPreviewOutDTO,
  CatalogCarrierDetailOutDTO,
  CompanyModuleForCarrierOutDTO,
  ModuleTypeEnum,
  Pageable,
  PermissionTypesEnum,
} from '@api/ringil-api.v2.tsx';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import SvgId from '@icons/id.svg?react';
import SvgTag from '@icons/tag-inverted.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgGlobe from '@icons/globe-inverted.svg?react';
import SvgTrash from '@icons/delete-trash.svg?react';
import SvgComponent from '@icons/component-inverted.svg?react';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { ApiContext } from '@api/api.tsx';
import RingilDataTable, { ColumnConfig, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import AddToCarrierGroupSidebar from '@app/pages/customer/carriers/carrier-detail/sidebar/AddToCarrierGroupSidebar.tsx';
import CarrierAdditionalInfoTag from '@components/framework/tags/CarrierAdditionalInfoTag.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface ComponentProps {
  carrierData: CatalogCarrierDetailOutDTO;
  reloadData: () => void;
}

type CarrierExportImportInfo = {
  exportCountries: string[];
  exportNote?: string;
  importCountries: string[];
  importNote?: string;
  cars: CatalogCarPreviewOutDTO[];
};

const CarrierDetailAbout: React.FC<ComponentProps> = ({ carrierData, reloadData }) => {
  const { tr } = useTranslationLgs();
  const { hasPermission } = useContext(AuthenticatedUserContext);
  const {
    cuDetailCarrierGroupsPage,
    cuRemoveGroupOnCarrierDetail,
    lovEnabledCompanyModule,
    setModuleToCompany,
  } = useContext(ApiContext);

  const [visibleAddToGroup, setVisibleAddToGroup] = useState(false);

  const editEnabled = hasPermission([PermissionTypesEnum.PERMISSION_CARRIER_GROUP_RW])
  const editModulesEnabled = hasPermission([PermissionTypesEnum.PERMISSION_MY_CARRIERS_RW])

  const carrierAndGroupsTableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);
  const modulesTableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);
  const regionsAndTrucksTableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  useEffect(() => {
    modulesTableRef.current?.reloadData();
  }, [carrierData]);

  const loadDataApiCarrierAndGroups = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<CarrierDetailGroupsOutDTO>) => void) => {
    cuDetailCarrierGroupsPage(
      carrierData.carrierId,
      pageable,
      data => {
        onSuccess(data);
      },
    );
  };

  const isRegionsDataAllEmpty =
    (!carrierData.exportCountries || carrierData.exportCountries.length === 0) &&
    (!carrierData.importCountries || carrierData.importCountries.length === 0) &&
    (!carrierData.cars || carrierData.cars.length === 0) &&
    !carrierData.exportNote &&
    !carrierData.importNote;

  const loadDataApiRegionsAndTrucks = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<CarrierExportImportInfo>) => void) => {
    const pagedData: PagedData<CarrierExportImportInfo> = {
      data: isRegionsDataAllEmpty
        ? []
        : [
          {
            exportCountries: carrierData.exportCountries,
            exportNote: carrierData.exportNote,
            importCountries: carrierData.importCountries,
            importNote: carrierData.importNote,
            cars: carrierData.cars,
          },
        ],
      total: isRegionsDataAllEmpty ? 0 : 1,
      page: pageable.pageNumber || 1,
      size: pageable.pageSize || 10,
    };

    onSuccess(pagedData);
  };

  const carrierAndGroupsColumnsConfig: ColumnConfig<CarrierDetailGroupsOutDTO>[] = [
    {
      id: 'name',
      header: 'CarrierDetailAbout.columnHeaderCarriersName',
      headerDefault: 'Name',
      width: '25%',
      sortable: true,
      field: 'name',
      order: 1,
      render: (data: CarrierDetailGroupsOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.name}
          </div>
        );
      },
    },
    {
      id: 'carrierCount',
      header: 'CarrierDetailAbout.columnHeaderCarriersCarrierCount',
      headerDefault: 'Carriers count',
      width: '20%',
      sortable: true,
      field: 'carrierCount',
      order: 2,
      render: (data: CarrierDetailGroupsOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.count}
          </div>
        );
      },
    },
    {
      id: 'createdBy',
      header: 'CarrierDetailAbout.columnHeaderCarriersCreatedBy',
      headerDefault: 'Created by',
      width: '20%',
      sortable: true,
      field: 'createdBy',
      order: 3,
      render: (data: CarrierDetailGroupsOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.createdBy}
          </div>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'CarrierDetailAbout.columnHeaderCarriersCreatedAt',
      headerDefault: 'Created at',
      width: '25%',
      sortable: true,
      field: 'createdAt',
      order: 4,
      render: (data: CarrierDetailGroupsOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.createdAt}
          </div>
        );
      },
    },
    {
      id: 'actions',
      width: '7%',
      sortable: false,
      order: 5,
      render: (data: CarrierDetailGroupsOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {editEnabled && (
              <RingilButton
                icon={<SvgTrash />}
                mode={'table'}
                onClick={() => {
                  cuRemoveGroupOnCarrierDetail(carrierData.carrierId, data.carrierGroupId, () => {
                    carrierAndGroupsTableRef.current?.reloadData();
                  });
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  const loadDataApiModules = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<CompanyModuleForCarrierOutDTO>) => void) => {
    lovEnabledCompanyModule(
      data => {
        const modules = carrierData.companyModules.filter(module => module.enabled).map(module => module.moduleType);

        const items = data.map(item => {
          const key = item as keyof typeof ModuleTypeEnum;
          const moduleType = ModuleTypeEnum[key] as ModuleTypeEnum;
          return {
            moduleType,
            enabled: modules.includes(moduleType),
          };
        });

        const pagedData: PagedData<CompanyModuleForCarrierOutDTO> = {
          data: items,
          total: items.length,
          page: 1,
          size: items.length,
        };
        onSuccess(pagedData);
      }
    );
  };

  const modulesColumnsConfig: ColumnConfig<CompanyModuleForCarrierOutDTO>[] = [
    {
      id: 'moduleType',
      header: 'CarrierDetailModules.columnHeaderModuleName',
      headerDefault: 'Name',
      width: '40%',
      order: 1,
      render: (data: CompanyModuleForCarrierOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.moduleType}
          </div>
        );
      },
    },
    {
      id: 'enabled',
      header: 'CarrierDetailModules.columnHeaderEnabled',
      headerDefault: 'Enabled',
      width: '40%',
      order: 2,
      render: (data: CompanyModuleForCarrierOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.enabled ? tr('CarrierDetailModules.enabled', 'Enabled') : tr('CarrierDetailModules.disabled', 'Disabled')}
          </div>
        );
      },
    },
    {
      id: 'actions',
      width: '20%',
      sortable: false,
      order: 3,
      render: (data: CompanyModuleForCarrierOutDTO) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {editModulesEnabled && (
              <RingilButton
                label={data.enabled ? tr('CarrierDetailModules.disable', 'Disable') : tr('CarrierDetailModules.enable', 'Enable')}
                mode={'table'}
                onClick={() => {
                  setModuleToCompany(carrierData.carrierId, data.moduleType, {enabled : !data.enabled}, () => {
                    reloadData();
                  })
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  const regionsAndTrucksColumnsConfig: ColumnConfig<any>[] = [
    {
      id: 'exportCountries',
      header: 'CarrierDetailAbout.columnHeaderRegionsExportCountries',
      headerDefault: 'Export countries',
      width: '20%',
      sortable: true,
      field: 'exportCountries',
      order: 1,
      render: (data: CarrierExportImportInfo) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.exportCountries.join(', ')}
          </div>
        );
      },
    },
    {
      id: 'exportNote',
      header: 'CarrierDetailAbout.columnHeaderRegionsExportNote',
      headerDefault: 'Export note',
      width: '20%',
      sortable: true,
      field: 'exportNote',
      order: 2,
      render: (data: CarrierExportImportInfo) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.exportNote}
          </div>
        );
      },
    },
    {
      id: 'importCountries',
      header: 'CarrierDetailAbout.columnHeaderRegionsImportCountries',
      headerDefault: 'Import countries',
      width: '20%',
      sortable: true,
      field: 'importCountries',
      order: 3,
      render: (data: CarrierExportImportInfo) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.importCountries.join(', ')}
          </div>
        );
      },
    },
    {
      id: 'importNote',
      header: 'CarrierDetailAbout.columnHeaderRegionsImportNote',
      headerDefault: 'Import note',
      width: '20%',
      sortable: true,
      field: 'importNote',
      order: 4,
      render: (data: CarrierExportImportInfo) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.importNote}
          </div>
        );
      },
    },
    {
      id: 'cars',
      header: 'CarrierDetailAbout.columnHeaderRegionsCars',
      headerDefault: 'Cars',
      width: '20%',
      sortable: true,
      field: 'cars',
      order: 5,
      render: (data: CarrierExportImportInfo) => {
        return (
          <div className={'flex flex-col items-start gap-2'}>
            {data.cars.map(car => `${car.name} (${car.count})`).join(', ')}
          </div>
        );
      },
    },
  ];


  return (
    <>
      <Ringil3BadgeRow>
        <ButtonTag
          label={tr('CarrierDetailAbout.tagType', 'Type')}
          value={carrierData.isMyCarrier ? tr('CarrierDetailAbout.ownCarrier', 'Own carrier') : tr('CarrierDetailAbout.externalCarrier', 'External carrier')}
          icon={<SvgId />}
          readonly
        />

        {carrierData.isAlreadyAdded && (
          <CarrierAdditionalInfoTag
            carrierId={carrierData.carrierId}
            carrierDetailAdditionalInfos={carrierData.additionalInfo}
            reloadData={reloadData}
          />
        )}
      </Ringil3BadgeRow>
      {carrierData.isAlreadyAdded && (
        <>
          <div className={'flex flex-row'}>
            <COHeadline
              variant={'h2'}
              title={tr('CarrierDetailAbout.headlineCarrierGroups', 'Carrier groups')}
              icon={<SvgTag className={'w-4 h-4 text-content-tertiary'} />}
            />
            {editEnabled && (
              <RingilButton
                icon={<SvgPlus />}
                label={tr('CarrierDetailAbout.buttonAddToCarrierGroups', 'Add to carrier group')}
                mode={'text-only'}
                onClick={() => setVisibleAddToGroup(true)}
              />
            )}
          </div>
          <RingilDataTable
            ref={carrierAndGroupsTableRef}
            columnConfig={carrierAndGroupsColumnsConfig as ColumnConfig<unknown>[]}
            loadDataFromApi={loadDataApiCarrierAndGroups}
            filterName={'carrier_detail_groups_list'}
          />

          <div className={'flex flex-row'}>
            <COHeadline
              variant={'h2'}
              title={tr('CarrierDetailAbout.headlineCarrierModules', 'Carrier modules')}
              icon={<SvgComponent className={'w-4 h-4 text-content-tertiary'} />}
            />
          </div>
          <RingilDataTable
            ref={modulesTableRef}
            columnConfig={modulesColumnsConfig as ColumnConfig<unknown>[]}
            loadDataFromApi={loadDataApiModules}
            filterName={'carrier_detail_modules_list'}
          />
        </>
      )}

      <div className={'flex flex-row mt-4'}>
        <COHeadline
          variant={'h2'}
          title={tr('CarrierDetailAbout.headlineRegionsAndTrucks', 'Covered regions and trucks')}
          icon={<SvgGlobe className={'w-4 h-4 text-content-tertiary'} />}
        />
      </div>
      <RingilDataTable
        ref={regionsAndTrucksTableRef}
        columnConfig={regionsAndTrucksColumnsConfig as ColumnConfig<unknown>[]}
        loadDataFromApi={loadDataApiRegionsAndTrucks}
        filterName={'carrier_detail_regions_list'}
      />

      <AddToCarrierGroupSidebar
        visible={visibleAddToGroup}
        onHide={() => setVisibleAddToGroup(false)}
        carrierId={carrierData?.carrierId}
        onSuccess={() => {
          setVisibleAddToGroup(false);
          carrierAndGroupsTableRef.current?.reloadData();
        }}
      />
    </>
  );
};


export default CarrierDetailAbout;
